// .dawproject export/import. The DAWproject open standard is a ZIP containing
// project.xml + media files. We write/read a pragmatic subset (tracks, channel
// volume/pan, audio clips referencing embedded WAVs) following the real element
// names so it interchanges with other DAWs at the structural level.
import { zipSync, unzipSync, strToU8, strFromU8 } from "fflate";
import { Project, newProject, newTrack, Clip, normalizeProject } from "../state/model";
import { Store } from "../state/store";
import { wavBytes } from "./export-wav";
import { uid } from "../util";

const esc = (s: string) => s.replace(/[<>&"']/g, (c) =>
  ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]!));

export function exportDawproject(store: Store): Blob {
  const p = store.project;
  const files: Record<string, Uint8Array> = {};
  const assetPath = new Map<string, string>();

  for (const asset of p.assets) {
    const buf = store.buffers.get(asset.id);
    if (!buf) continue;
    const path = `audio/${asset.id}.wav`;
    assetPath.set(asset.id, path);
    files[path] = wavBytes(buf);
  }

  const trackXml = p.tracks.map((t) => `
    <Track contentType="${t.kind === "instrument" ? "notes" : "audio"}" loaded="true" id="${t.id}" name="${esc(t.name)}">
      <Channel role="regular" audioChannels="2" id="ch-${t.id}">
        <Volume unit="linear" value="${t.volume}" id="vol-${t.id}"/>
        <Pan unit="normalized" value="${(t.pan + 1) / 2}" id="pan-${t.id}"/>
        <Mute value="${t.mute}" id="mute-${t.id}"/>
      </Channel>
    </Track>`).join("");

  const lanesXml = p.tracks.map((t) => `
      <Lanes track="${t.id}" id="lane-${t.id}">
        <Clips id="clips-${t.id}">${t.clips.map((c) => {
          const a = store.asset(c.assetId);
          // MIDI clip → DAWproject <Notes>; audio clip → <Audio> referencing a WAV
          const inner = c.notes
            ? `
            <Notes>${c.notes.map((n) => `
              <Note time="${n.start}" duration="${n.dur}" channel="0" key="${n.midi}" vel="${n.vel}"/>`).join("")}
            </Notes>`
            : `
            <Audio algorithm="raw" channels="${a?.channels ?? 2}" duration="${a?.duration ?? c.duration}" sampleRate="${a?.sampleRate ?? 48000}" id="audio-${c.id}">
              <File path="${assetPath.get(c.assetId) ?? ""}"/>
            </Audio>`;
          return `
          <Clip time="${c.start}" duration="${c.duration}" playStart="${c.offset}" name="${esc(c.name)}" id="${c.id}">${inner}
          </Clip>`;
        }).join("")}
        </Clips>
      </Lanes>`).join("");

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Project version="1.0">
  <Application name="WebDAW" version="0.1"/>
  <Transport>
    <Tempo unit="bpm" value="${p.tempo}" id="tempo"/>
    <TimeSignature numerator="${p.beatsPerBar}" denominator="4" id="tsig"/>
  </Transport>
  <Structure>${trackXml}
  </Structure>
  <Arrangement id="arrangement">
    <Lanes timeUnit="seconds" id="lanes">${lanesXml}
    </Lanes>
  </Arrangement>
</Project>`;

  const metadata = `<?xml version="1.0" encoding="UTF-8"?>
<MetaData><Title>${esc(p.name)}</Title></MetaData>`;

  files["project.xml"] = strToU8(xml);
  files["metadata.xml"] = strToU8(metadata);
  // Lossless WebDAW sidecar: the standard project.xml can't represent our
  // instruments (synth/drums/gm/sampler/wam), FX chains, takes, etc. Embed the
  // full project so re-opening in WebDAW restores everything exactly; audio
  // buffers live in the audio/*.wav files referenced by asset id.
  files["webdaw.json"] = strToU8(JSON.stringify(JSON.parse(JSON.stringify(p))));

  const zipped = zipSync(files, { level: 6 });
  return new Blob([zipped as BlobPart], { type: "application/zip" });
}

export async function importDawproject(file: ArrayBuffer, store: Store, ctx: AudioContext): Promise<void> {
  const files = unzipSync(new Uint8Array(file));

  // WebDAW-native sidecar → lossless restore (instruments, MIDI notes, FX, takes).
  if (files["webdaw.json"]) {
    const proj: Project = JSON.parse(strFromU8(files["webdaw.json"]));
    store.buffers.clear();
    for (const asset of proj.assets) {
      const bytes = files[`audio/${asset.id}.wav`];
      if (!bytes) continue;
      try { store.buffers.set(asset.id, await ctx.decodeAudioData(bytes.slice().buffer)); } catch { /* skip undecodable */ }
    }
    store.project = normalizeProject(proj);
    store.select(proj.tracks[0]?.id ?? null);
    store.emitChange("project");
    return;
  }

  const xmlText = strFromU8(files["project.xml"] ?? new Uint8Array());
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");

  const proj: Project = newProject(strFromU8(files["metadata.xml"] ? files["metadata.xml"] : new Uint8Array()).match(/<Title>(.*?)<\/Title>/)?.[1] || "Imported Project");
  proj.tracks = [];
  proj.assets = [];
  store.buffers.clear();

  const tempo = doc.querySelector("Transport > Tempo");
  if (tempo) proj.tempo = Number(tempo.getAttribute("value")) || 120;
  const tsig = doc.querySelector("Transport > TimeSignature");
  if (tsig) proj.beatsPerBar = Number(tsig.getAttribute("numerator")) || 4;

  const trackEls = Array.from(doc.querySelectorAll("Structure > Track"));
  let idx = 0;
  const trackById = new Map<string, ReturnType<typeof newTrack>>();
  for (const tEl of trackEls) {
    const t = newTrack(idx++, tEl.getAttribute("contentType") === "notes" ? "instrument" : "audio");
    t.id = tEl.getAttribute("id") || t.id;
    t.name = tEl.getAttribute("name") || t.name;
    const vol = tEl.querySelector("Channel > Volume");
    if (vol) t.volume = Number(vol.getAttribute("value")) || 1;
    const pan = tEl.querySelector("Channel > Pan");
    if (pan) t.pan = (Number(pan.getAttribute("value")) || 0.5) * 2 - 1;
    const mute = tEl.querySelector("Channel > Mute");
    if (mute) t.mute = mute.getAttribute("value") === "true";
    proj.tracks.push(t);
    trackById.set(t.id, t);
  }

  const laneEls = Array.from(doc.querySelectorAll("Arrangement Lanes > Lanes[track]"));
  for (const lane of laneEls) {
    const track = trackById.get(lane.getAttribute("track") || "");
    if (!track) continue;
    for (const clipEl of Array.from(lane.querySelectorAll("Clips > Clip"))) {
      // MIDI clip (DAWproject <Notes>) → rebuild note data, no audio asset
      const notesEl = clipEl.querySelector("Notes");
      if (notesEl) {
        const notes = Array.from(notesEl.querySelectorAll("Note")).map((n) => ({
          midi: Number(n.getAttribute("key")) || 60,
          start: Number(n.getAttribute("time")) || 0,
          dur: Number(n.getAttribute("duration")) || 0.25,
          vel: Number(n.getAttribute("vel")) || 0.8,
        }));
        track.clips.push({
          id: clipEl.getAttribute("id") || uid("clip"),
          name: clipEl.getAttribute("name") || "MIDI", assetId: "",
          start: Number(clipEl.getAttribute("time")) || 0,
          duration: Number(clipEl.getAttribute("duration")) || 1,
          offset: Number(clipEl.getAttribute("playStart")) || 0,
          gain: 1, fadeIn: 0, fadeOut: 0, notes,
        });
        continue;
      }
      const audioEl = clipEl.querySelector("Audio");
      const path = audioEl?.querySelector("File")?.getAttribute("path") || "";
      const bytes = files[path];
      if (!bytes) continue;
      let buffer: AudioBuffer;
      try { buffer = await ctx.decodeAudioData(bytes.slice().buffer); } catch { continue; }
      const assetId = uid("ast");
      store.buffers.set(assetId, buffer);
      proj.assets.push({
        id: assetId, name: clipEl.getAttribute("name") || "clip",
        sampleRate: buffer.sampleRate, channels: buffer.numberOfChannels,
        length: buffer.length, duration: buffer.duration,
      });
      const clip: Clip = {
        id: clipEl.getAttribute("id") || uid("clip"),
        name: clipEl.getAttribute("name") || "clip",
        assetId,
        start: Number(clipEl.getAttribute("time")) || 0,
        duration: Number(clipEl.getAttribute("duration")) || buffer.duration,
        offset: Number(clipEl.getAttribute("playStart")) || 0,
        gain: 1, fadeIn: 0.005, fadeOut: 0.005,
      };
      track.clips.push(clip);
    }
  }

  store.project = proj;
  store.select(proj.tracks[0]?.id ?? null);
  store.emitChange("project");
}
